import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loading } from '@bumble/app/components/Loading';
import { useApi } from '@bumble/app/hooks/use-api';
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import Select from 'react-select';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import CreateModal from '@bumble/app/components/CreateModal';
import SplitPanes from '@bumble/app/components/SplitPanes';
import MultipleSelections from '@bumble/app/components/MultipleSelections';
import { Indicator } from '@bumble/app/components/Indicator';
import { toast } from 'react-toastify';
import { EditPduInfo, PduCreate, PduOutlets } from '@bumble/app/components/pdu';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import WifiFindOutlinedIcon from '@mui/icons-material/WifiFindOutlined';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import { PageLoading } from '@bumble/app/components/PageLoading';
import { apiError } from '../utils/api-error';
import DialogStandard from '../components/DialogStandard';

export const PDUManagement = () => {
  const api = useApi();

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [cpes, setCpes] = useState<any[]>([{ label: 'Select a CPE…', value: 'none'}]);
  const [selectedCpe, setSelectedCpe] = useState<any>({ label: 'Select a CPE…', value: 'none'});
  const [recoverededIP, setRecoveredIP] = useState<string>('');
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [showEditPDUInfo, setShowEditPDUInfo] = useState<boolean>(false);
  const [showEditPDUPopUp, setShowEditPDUPopUp] = useState<boolean>(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
  const [menuItems, setMenuItems] = useState<{
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    hasDivider?: boolean;
    disabled?: boolean;
  }[]>([]);

  const infoURL = 'https://www.bumblebeenet.com/post/oob-remote-pdu-management';

  const { data: cpeData } = useQuery(
    ['routers'],
    api.getCpeApiV1GetCpeGet.bind(null, {}),
    { enabled: true }
  );

  const {
    isLoading: loadingPdu,
    data: pduData,
    refetch: pduRefetch,
    isRefetching: refetchingPdu,
  } = useQuery(
    ['pdu-management', selectedCpe?.value],
    () => {
      return api.getPduApiV1GetPduGet({
        node_id: selectedCpe.value,
      });
    },
    {
      enabled: !!selectedCpe?.value && selectedCpe.value !== 'none',
      retry: false,
      onError: (error: any) => {
        if (error.message !== 'No CPE selected') {
          apiError(error);
        }
      },
    }
  );

  const editPduInfo = useMutation(api.editPduInfoApiV1EditPduInfoPost, {
    onSuccess() {
      toast.success('PDU Info updated successfully.');
      setShowEditPDUPopUp(false);
      handleDataRefetch();
    },
    onError: apiError,
  });

  const recoverIPPost = useMutation(api.recoverPduIpApiV1RecoverPduIpPost,
    {
      onSuccess:(res: any) => {
        toast.success('Recovered IP successfully.');
        setLoadingModalOpen(false);
        setRecoveredIP(res.data.ip);
        setShowEditPDUPopUp(true);
      },
      onError(error: any) {
        apiError(error);
        setLoadingModalOpen(false);
        setShowConfirmDelete(false);
      },
    }
  )

  const discoverPDUPost = useMutation(
    api.discoverPduApiV1DiscoverPduPost,
    {
      onSuccess: () => {
        toast.success('PDU discovery completed successfully.');
        handleDataRefetch();
      },
      onError: apiError,
    }
  );

  const selectedRowItem = useMemo(() => {
    return rowSelectionModel.length === 1 ? rowSelectionModel[0].toString() : '';
  }, [rowSelectionModel]);

  const selectedPdu = useMemo(() => {
    if (!selectedRowItem || !pduData?.data) return undefined;
    return pduData.data.find((pdu: any) => pdu.pdu_id === selectedRowItem);
  }, [selectedRowItem, pduData]);

  const {
    data: detailData,
    refetch: detailRefetch,
    isRefetching: refetchingDetail,
  } = useQuery(
    ['pduDetail', selectedRowItem],
    api.getPduDetailApiV1GetPduDetailGet.bind(null, {
      pdu_id: selectedRowItem,
    }),
    { enabled: !!selectedRowItem },
  );

  useEffect(() => {
    if (selectedRowItem) detailRefetch();
  }, [detailRefetch, selectedRowItem]);

  useEffect(() => {
    const entity_data = cpeData?.data;
    let actualEntities = [{ label: 'Select a CPE…', value: 'none' }];
    if (entity_data?.length) {
      actualEntities = actualEntities.concat(entity_data.map((e: any) => ({
        label: e.cpe_name,
        value: e.cpe_id,
      })));
    }
    setCpes(actualEntities);
    if (!selectedCpe || !actualEntities.find(c => c.value === selectedCpe?.value)) {
      setSelectedCpe(actualEntities[0]);
    }
  }, [cpeData]);

  const columns: GridColDef[] = [
    {
      field: 'pdu_name',
      headerName: 'PDU Name',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'reachable',
      headerName: 'Status (Reachable)',
      width: 150,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          {params.value !== undefined ? <Indicator active={params.value === true} /> : 'N/A'}
        </Box>
      ),
    },
    {
      field: 'ip_addr',
      headerName: 'IP Address',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'vendor_name',
      headerName: 'Manufacturer',
      width: 250,
      disableColumnMenu: true,
    },
    {
      field: 'model_name',
      headerName: 'Model',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'serial_number',
      headerName: 'Serial Number',
      width: 250,
      disableColumnMenu: true,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 200,
      disableColumnMenu: true,
    },
  ];

  const changeCpe = (val: any) => {
    setSelectedCpe(val);
    setRowSelectionModel([]);
  };

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection.slice(-1));
  };

  const removePdu = useMutation(
    api.removePduApiV1RemovePduPost,
    {
      onSuccess() {
        toast.success('PDU removed successfully.');
        setLoadingModalOpen(false);
        setShowConfirmDelete(false);
        handleDataRefetch();
        setRowSelectionModel([]);
      },
      onError(error: any) {
        apiError(error);
        setLoadingModalOpen(false);
        setShowConfirmDelete(false);
      },
    }
  );

  useEffect(() => {
    const items = [
      {
        label: 'Edit PDU Info',
        icon: <EditIcon color="info" fontSize="small" />,
        onClick: () => {
          setShowEditPDUInfo(true);
        },
        hasDivider: true,
      },
      {
        label: 'Recover IP',
        icon: <WifiFindOutlinedIcon color="info" fontSize="small" />,
        onClick: () => {
          handleRecoverIP();
        },
        hasDivider: true,
      },
      {
        label: 'Delete',
        icon: <DeleteForeverOutlinedIcon color="error" fontSize="small" />,
        onClick: () => {
          setShowConfirmDelete(true);
        },
        hasDivider: true,
      },
    ];
    setMenuItems([...items]);
  }, [selectedRowItem]);

  const handleRecoverIP = () => {
    setLoadingModalOpen(true);
    recoverIPPost.mutate({
      pdu_id: selectedRowItem
    });
  }

  const handlePduDelete = () => {
    setLoadingModalOpen(true);
    removePdu.mutate({ pdu_id: selectedRowItem });
  };

  const handleDataRefetch = () => {
    pduRefetch();
    toast.success('Refreshed PDU devices.');
  };

  const handleCreateModalOpen = () => {
    if (!selectedCpe || selectedCpe.value === 'none') {
      toast.error('Please select a CPE first');
      return;
    }
    setOpenCreateModal(true);
  };

  const handleEditPopUpOpen = () => {
    editPduInfo.mutate({
      pdu_id: selectedRowItem,
      ip_addr: recoverededIP
    })
  }

  const handleDiscoverPDU = () => {
    discoverPDUPost.mutate({
      node_id: selectedCpe.value
    })
  }

  const handleCreateModalClose = () => {
    setOpenCreateModal(false);
    handleDataRefetch();
  };

  return (
    <PageContent>
      <PageHeader pageName="PDU Management" />
      <Box sx={{ display: 'inline-flex', mb: 5, position: 'relative', gap: 2 }}>
        <Select options={cpes} value={selectedCpe} onChange={changeCpe} styles={{
            control: (baseStyles) => ({
              ...baseStyles,
              borderRadius: '8px',
              width: '300px',
            }),
            menu: (baseStyles) => ({
              ...baseStyles,
              width: '300px',
            }),
        }}/>
        <Badge badgeContent={0} color="secondary">
          <Box
            component="a"
            href={infoURL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Tooltip arrow title="Info">
              <IconButton color="primary" size="small">
                <InfoOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Badge>
      </Box>
      {loadingPdu && selectedCpe.value !== 'none' ? (
        <Loading />
      ) : (
        <SplitPanes
          defaultSizes={['30%', '70%']}
          panePadding={8}
          top={
            <Card elevation={0}>
              <CardContent>
                <DataTableGrid
                  rows={
                    pduData?.data?.map((item: any) => ({
                      ...item,
                      id: item.pdu_id,
                    })) || []
                  }
                  columns={columns}
                  pagination
                  rowSelectionModel={rowSelectionModel}
                  handleSelectionChange={handleSelectionChange}
                  itemsSelected={rowSelectionModel.length === 1}
                  refreshData={handleDataRefetch}
                  isRefetching={refetchingPdu}
                  refreshDisabled={!selectedCpe || selectedCpe.value === 'none'}
                  newLabel="Add PDU"
                  newOnClick={handleCreateModalOpen}
                  newDisabled={!selectedCpe || selectedCpe.value === 'none'}
                  extraBtnLabel="Discover PDU"
                  extraBtnOnClick={handleDiscoverPDU}
                  extraBtnDisabled={!selectedCpe || selectedCpe.value === 'none' || discoverPDUPost.isLoading}
                  menuItems={menuItems}
                />
              </CardContent>
            </Card>
          }
          bottom={
            <Card elevation={0} sx={{ mt: 5 }}>
              <CardContent>
                {rowSelectionModel.length === 1 ? (
                  <PduOutlets
                    data={detailData?.data || selectedPdu}
                    onOutletUpdate={() => detailRefetch()}
                    isRefetching={refetchingDetail}
                  />
                ) : (
                  <MultipleSelections count={rowSelectionModel.length} />
                )}
              </CardContent>
            </Card>
          }
        />
      )}
      {openCreateModal && selectedCpe && selectedCpe.value !== 'none' && (
        <CreateModal
          open={openCreateModal}
          handleClose={handleCreateModalClose}
          title="Add PDU"
        >
          <PduCreate handleClose={handleCreateModalClose} nodeId={selectedCpe.value} />
        </CreateModal>
      )}
      {
        showEditPDUInfo && (
          <DialogStandard
            open={showEditPDUInfo}
            handleClose={() => setShowEditPDUInfo(false)}
            title="Edit PDU Info"
          >
            <EditPduInfo
              onSubmit={() => {
                setShowEditPDUInfo(false);
                pduRefetch();
                detailRefetch();
              }}
              pduId={selectedRowItem}
              ipAddress={detailData?.data.ip_addr || ''}
              pduName={detailData?.data.pdu_name || ''}
              description={detailData?.data.description || ''}
            />
          </DialogStandard>
        )
      }
      {showConfirmDelete && (
        <DialogConfirm
          open={showConfirmDelete}
          handleClose={() => setShowConfirmDelete(false)}
          handleConfirm={handlePduDelete}
          title="Delete PDU?"
          description={`Are you certain you wish to proceed with deleting ${selectedRowItem}? Please note that this action is irreversible and will result in the permanent removal of PDU device.`}
          confirmationPhrase={selectedRowItem}
        />
      )}
      {loadingModalOpen && <PageLoading content="Working on it..." />}
      <Dialog
        open={showEditPDUPopUp}
        maxWidth="sm"
        fullWidth
      >
        {editPduInfo.isLoading && <PageLoading content="Updating PDU Info..." />}
        <DialogContent sx={{
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center'
        }}>
          <Typography sx={{ mb: 2 }}>
            The PDU IP has changed to {recoverededIP}.
          </Typography>
          <Typography sx={{ mb: 2 }}>
            Click OK to update PDU with the new IP.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', alignItems: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => {
              handleEditPopUpOpen();
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </PageContent>
  );
};
